import React, { useState, useEffect, useRef } from 'react'
import { usePlanLapse } from '@/hooks/usePlanLapse'
import { createPortal } from 'react-dom'
import { downloadMaterial } from '@/pages/materials/downloadMaterial'
import { Play, CheckCircle, XCircle, CircleNotch, Clock, CaretUp, FileText, Paperclip, ArrowsOut, DownloadSimple, X } from '@phosphor-icons/react'
import { Button } from '@/components/ui/Button'
import { Select } from '@/components/ui/Select'
import { Tooltip } from '@/components/ui/Tooltip'
import { workflowsApi, contactsApi, materialsApi, presentationsApi } from '@/lib/api'
import { SendEmailTestConfig } from './test-configs/SendEmailTestConfig'
import { EnrichmentResultView } from './EnrichmentResultView'
import { SmsTestConfig } from './test-configs/SmsTestConfig'
import { LinkedInTestConfig } from './test-configs/LinkedInTestConfig'
import { describeTestRequestError } from './testNodeRequestError'
import type { NodeType, WorkflowNodeData } from '@/types/workflow'

/**
 * Turn a raw SMTP / transport error into a message a non-technical user can act
 * on. Returns the plain-English message plus (when a mapping matched) the exact
 * original error as `technical`, so the precise wording is never lost. When no
 * mapping matches, the raw error is surfaced verbatim as the message.
 */
function humanizeSendError(raw?: string): { message: string; technical?: string } {
  if (!raw || !raw.trim()) {
    return { message: 'Something went wrong while sending. Please try again.' }
  }
  const e = raw.toLowerCase()
  const map = (message: string) => ({ message, technical: raw })

  if (e.includes('unexpectedly closed') || e.includes('connection reset') || e.includes('connection aborted') || e.includes('broken pipe')) {
    return map('The connection to the email server dropped before the message finished sending. This is usually a wrong SMTP port or an SSL/TLS mismatch — check this sender’s server settings and try again.')
  }
  if (e.includes('connection refused') || e.includes('could not connect') || e.includes('cannot connect') || e.includes('name or service not known') || e.includes('getaddrinfo') || e.includes('failed to resolve') || e.includes('nodename nor servname')) {
    return map('Couldn’t reach the email server. Double-check the SMTP host and port for this sender.')
  }
  if (e.includes('timed out') || e.includes('timeout')) {
    return map('The email server took too long to respond. Check the SMTP host and port, then try again.')
  }
  if (e.includes('authentication') || e.includes('535') || e.includes('username and password') || e.includes('not accepted') || e.includes('invalid login') || e.includes('credentials') || e.includes('auth ')) {
    return map('The sender’s email login was rejected. Re-check the username and password — for Gmail or Outlook you usually need an app password, not your normal one.')
  }
  if (e.includes('ssl') || e.includes('tls') || e.includes('certificate') || e.includes('wrong version number')) {
    return map('The secure connection to the email server failed. Check the SSL/TLS setting for this sender (port 465 = SSL, port 587 = STARTTLS).')
  }
  if (e.includes('relay') || e.includes('550') || e.includes('recipient') || e.includes('does not exist') || e.includes('no such user') || e.includes('mailbox unavailable')) {
    return map('The email server rejected the recipient address. Make sure the recipient’s email is valid.')
  }
  if (e.includes('553') || e.includes('from address') || e.includes('sender address') || e.includes('not allowed to send') || e.includes('not verified')) {
    return map('The email server rejected the sender address. Make sure this sender is verified and allowed to send.')
  }
  if (e.includes('quota') || e.includes('rate limit') || e.includes('too many') || e.includes('452') || e.includes('421')) {
    return map('The email server is temporarily refusing new messages (sending limit reached). Wait a little and try again.')
  }
  // No mapping — surface the exact error, just tidied to start with a capital.
  return { message: raw.charAt(0).toUpperCase() + raw.slice(1) }
}

// Friendly labels for the success-detail keys the node testers return.
const RESULT_KEY_LABELS: Record<string, string> = {
  recipient: 'Recipient', recipient_email: 'Recipient', to_number: 'To', to: 'To',
  subject: 'Subject', body: 'Preview', message_body: 'Message', message_sent: 'Message',
  smtp_response: 'Server response', message_id: 'Message ID', message_sid: 'Message SID',
  status: 'Status', delivery_status: 'Delivery status', channel: 'Channel',
  provider: 'Provider', model: 'Model', title: 'Title', filename: 'File',
}

function humanizeKey(k: string): string {
  return RESULT_KEY_LABELS[k] ?? k.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())
}

function formatResultValue(v: unknown): string {
  if (v === null || v === undefined || v === '') return '—'
  if (typeof v === 'string') return v.length > 400 ? `${v.slice(0, 400)}…` : v
  if (typeof v === 'number' || typeof v === 'boolean') return String(v)
  try {
    const s = JSON.stringify(v)
    return s.length > 400 ? `${s.slice(0, 400)}…` : s
  } catch {
    return String(v)
  }
}

/**
 * Clean, readable key/value rendering of a test result's `details` — replaces
 * the raw JSON dump so the success card harmonizes with the failure card.
 */
const ResultMeta: React.FC<{ details: Record<string, any> }> = ({ details }) => {
  const entries = Object.entries(details).filter(([, v]) => v !== null && v !== undefined && v !== '')
  if (entries.length === 0) return null
  return (
    <div className="mt-3 rounded-lg border border-border/50 bg-muted/30 dark:bg-white/[0.02] dark:border-white/[0.06] divide-y divide-border/40 dark:divide-white/[0.05]">
      {entries.map(([k, v]) => (
        <div key={k} className="flex items-start gap-3 px-3 py-2">
          <span className="w-28 flex-shrink-0 pt-0.5 text-[10px] font-medium uppercase tracking-[0.06em] text-muted-foreground">
            {humanizeKey(k)}
          </span>
          <span className="min-w-0 flex-1 break-words text-[13px] text-foreground/90 dark:text-[#d0d6e0]">
            {formatResultValue(v)}
          </span>
        </div>
      ))}
    </div>
  )
}

const TESTABLE_TYPES = new Set<NodeType>([
  'sendEmail', 'httpRequest', 'internalAlert', 'smsMessage',
  'linkedInAction', 'enrichContacts', 'generateDeck', 'aiDecision',
])

const NEEDS_CONTACT = new Set<NodeType>([
  'linkedInAction', 'enrichContacts', 'aiDecision',
])

interface TestNodePanelProps {
  nodeType: NodeType
  nodeData: WorkflowNodeData
  /** Pre-selected contact (from playground). If absent, shows contact picker. */
  contactId?: number
  /** Deck attachment from an upstream generateDeck step (playground).
   *  May carry any of:
   *  - ``download_url``: already-rendered AI deck under
   *    ``/api/presentations/download/<uid>/<file>``.
   *  - ``material_id``: attach_direct mode — references an existing
   *    MarketingMaterial; backend resolves the file path itself.
   *  - ``slides`` + ``title``: AI deck whose slide JSON exists but has
   *    not been rendered to a file yet. We render it on demand below
   *    before sending so the deck arrives as case 1. */
  deckAttachment?: {
    download_url?: string
    filename?: string
    material_id?: number
    slides?: any[]
    title?: string
    color_theme?: Record<string, string>
    logo_url?: string
  }
  /** Playground layout: render config + a single "Run Test" button inline,
   *  skipping the collapsed "Test This Node" accordion trigger. */
  alwaysOpen?: boolean
  /** Content already generated and shown to the user for an AI-mode sendEmail
   *  node — the config panel's "Preview" tab, or the current Playground step's
   *  generated email. When set, the actual test send reuses this content
   *  instead of asking the backend to run the LLM generation a second time,
   *  so the sent email always matches what was already reviewed and no extra
   *  credit is spent on a redundant generation (AB#21906). */
  pregeneratedEmail?: { subject: string; body: string }
  /** Called once a Run Test finishes, with whether it succeeded. The playground
   *  uses it to turn an enrichment step's "Skip & Continue" into "Next step"
   *  once the step has really run (AB#22529). */
  onResult?: (outcome: { success: boolean }) => void
}

interface TestResult {
  success: boolean
  node_type: string
  // requestFailed: the test request itself failed; `message` is already the
  // plain sentence for the user, so no raw error line is shown under it.
  result: { message: string; error?: string; requestFailed?: boolean; details?: Record<string, any> }
  duration_ms: number
}

export const TestNodePanel: React.FC<TestNodePanelProps> = ({ nodeType, nodeData, contactId: preselectedContactId, deckAttachment, alwaysOpen, pregeneratedEmail, onResult }) => {
  const { lapsed: planLapsed, reason: lapsedTitle } = usePlanLapse()
  const [isOpen, setIsOpen] = useState(false)
  const [loading, setLoading] = useState(false)
  const [elapsedMs, setElapsedMs] = useState(0)
  const [testResult, setTestResult] = useState<TestResult | null>(null)

  // Contact picker state (only when no preselected contact)
  const [contacts, setContacts] = useState<{ id: number; full_name: string; email: string }[]>([])
  const [selectedContactId, setSelectedContactId] = useState<number | undefined>(preselectedContactId)
  const [contactsLoaded, setContactsLoaded] = useState(false)

  // Test config state per node type
  const [emailConfig, setEmailConfig] = useState<{
    send_to: 'self' | 'test_address' | 'real_contact'
    test_email: string
    sender_email_id: number | null
  }>({
    send_to: 'self',
    test_email: '',
    sender_email_id: null,
  })
  const [smsConfig, setSmsConfig] = useState<{
    send_to: 'self' | 'test_address' | 'real_contact'
    test_phone: string
  }>({
    send_to: 'self',
    test_phone: '',
  })

  const needsContact = NEEDS_CONTACT.has(nodeType) ||
    (nodeType === 'sendEmail' && emailConfig.send_to === 'real_contact') ||
    (nodeType === 'smsMessage' && smsConfig.send_to === 'real_contact')

  // Show the picker as OPTIONAL for sendEmail/smsMessage when not in real_contact
  // mode, so users can render {{contact.*}} variables against a real contact even
  // when the test message goes to self / a test address. Backend honors this:
  // _resolve_recipient_email only uses contact.email when send_to === 'real_contact'.
  const showOptionalContactPicker =
    !needsContact && (nodeType === 'sendEmail' || nodeType === 'smsMessage')

  const loadContacts = () => {
    if (contactsLoaded) return
    contactsApi.getAll().then(res => {
      setContacts(res.data.map((c: any) => ({ id: c.id, full_name: c.full_name || '', email: c.email })))
      setContactsLoaded(true)
    }).catch(() => {})
  }

  const handleOpen = () => {
    setIsOpen(true)
    if (!preselectedContactId) loadContacts()
  }

  // Always-open (playground) layout renders the picker inline, so load contacts
  // up front instead of on an accordion open that never happens.
  useEffect(() => {
    if (alwaysOpen && !preselectedContactId) loadContacts()
     
  }, [alwaysOpen, preselectedContactId])

  // The contact picked under "Real contact" mode is that mode's recipient —
  // it doesn't carry meaning once "Send To" switches to self/test address, so
  // leaving 'real_contact' clears the selection. Without this, the picker
  // (now relabeled "Render variables using (optional)") kept showing the
  // contact chosen as the real-contact recipient, reading as if the Real
  // Contact section never went away.
  const prevEmailSendTo = useRef(emailConfig.send_to)
  useEffect(() => {
    if (prevEmailSendTo.current === 'real_contact' && emailConfig.send_to !== 'real_contact' && !preselectedContactId) {
      setSelectedContactId(undefined)
    }
    prevEmailSendTo.current = emailConfig.send_to
  }, [emailConfig.send_to, preselectedContactId])

  const prevSmsSendTo = useRef(smsConfig.send_to)
  useEffect(() => {
    if (prevSmsSendTo.current === 'real_contact' && smsConfig.send_to !== 'real_contact' && !preselectedContactId) {
      setSelectedContactId(undefined)
    }
    prevSmsSendTo.current = smsConfig.send_to
  }, [smsConfig.send_to, preselectedContactId])

  // "Real contact" isn't offered as a Send To mode in the Playground
  // (alwaysOpen) — it already runs the whole simulation against one chosen
  // contact, and letting someone pick "Real contact" there risks a real
  // email going out to a real person while they're just clicking through a
  // simulation. Defensively reset if this state carried over from a prior
  // step of the same node type (React reuses the component instance when
  // consecutive steps share a node type, since TestNodePanel isn't keyed).
  useEffect(() => {
    if (alwaysOpen && emailConfig.send_to === 'real_contact') {
      setEmailConfig(prev => ({ ...prev, send_to: 'self' }))
    }
  }, [alwaysOpen, emailConfig.send_to])

  // Don't render for non-testable nodes.
  // This guard MUST stay below every hook above. It used to sit at the very top
  // of the component, which meant the 9 useState / 2 useRef / 4 useEffect calls
  // ran only for testable node types. Because this component is not keyed by
  // node type, React reuses one instance across consecutive steps that differ
  // in type — so selecting a testable node and then a non-testable one changed
  // the hook count between renders on the same instance and threw
  // "Rendered fewer hooks than expected. This may be caused by an accidental
  // early return statement." Hooks are now unconditional; only the render bails.
  if (!TESTABLE_TYPES.has(nodeType)) return null

  const buildTestConfig = () => {
    if (nodeType === 'sendEmail' || (nodeType === 'internalAlert' && nodeData.internal_alert_config?.channel === 'email')) {
      const isAiSendEmail = nodeType === 'sendEmail' && nodeData.ai_config?.generation_mode === 'ai'
      return {
        send_to: emailConfig.send_to,
        test_email: emailConfig.test_email || undefined,
        sender_email_id: emailConfig.sender_email_id || undefined,
        // Reuse whatever AI content the user has already reviewed (Preview
        // tab / Playground step) so the test send doesn't trigger a second,
        // independent LLM generation — see node_tester.py's _test_send_email.
        ...(isAiSendEmail && pregeneratedEmail ? {
          pregenerated_subject: pregeneratedEmail.subject,
          pregenerated_body: pregeneratedEmail.body,
        } : {}),
      }
    }
    if (nodeType === 'smsMessage') {
      return {
        send_to: smsConfig.send_to,
        test_phone: smsConfig.test_phone || undefined,
      }
    }
    return undefined
  }

  const handleRunTest = async () => {
    setLoading(true)
    setTestResult(null)
    setElapsedMs(0)

    const startTime = Date.now()
    const timer = setInterval(() => setElapsedMs(Date.now() - startTime), 100)

    try {
      // Materialize the upstream deck reference before sending. The
      // playground's /preview endpoint returns slide JSON only — no
      // PPTX file — so an AI-generated deck arrives here without a
      // download_url. Render it now (no LLM, ~1–3s) so the backend
      // can resolve a real blob path to attach.
      let resolvedDeck = deckAttachment
      if (
        resolvedDeck
        && !resolvedDeck.download_url
        && !resolvedDeck.material_id
        && resolvedDeck.slides && resolvedDeck.slides.length > 0
      ) {
        try {
          const render = await presentationsApi.renderSlides({
            title: resolvedDeck.title || 'Presentation',
            slides: resolvedDeck.slides,
            color_theme: resolvedDeck.color_theme,
            output_format: 'pptx',
            logo_url: resolvedDeck.logo_url,
          })
          resolvedDeck = {
            ...resolvedDeck,
            download_url: render.data.download_url,
            filename: render.data.filename,
          }
        } catch {
          // Fall through — backend will simply send without the attachment.
        }
      }

      const deckPayload =
        resolvedDeck && (resolvedDeck.download_url || resolvedDeck.material_id)
          ? {
              download_url: resolvedDeck.download_url,
              filename: resolvedDeck.filename,
              material_id: resolvedDeck.material_id,
            }
          : undefined

      const response = await workflowsApi.testNode({
        node_type: nodeType,
        node_data: nodeData,
        contact_id: selectedContactId || null,
        test_config: buildTestConfig(),
        deck_attachment: deckPayload,
      })
      setTestResult(response.data)
      const data = response.data as TestResult
      onResult?.({ success: !!data.success && !data.result?.error && !data.result?.details?.error })
    } catch (error: any) {
      setTestResult({
        success: false,
        node_type: nodeType,
        result: {
          message: describeTestRequestError(error),
          requestFailed: true,
        },
        duration_ms: Date.now() - startTime,
      })
      onResult?.({ success: false })
    } finally {
      clearInterval(timer)
      setLoading(false)
    }
  }

  const canRun = () => {
    if (needsContact && !selectedContactId) return false
    if (nodeType === 'sendEmail' && !emailConfig.sender_email_id) return false
    if (nodeType === 'sendEmail' && emailConfig.send_to === 'test_address' && !emailConfig.test_email) return false
    if (nodeType === 'smsMessage' && smsConfig.send_to !== 'real_contact' && !smsConfig.test_phone) return false
    return true
  }

  // Reusable Run Test button + result block (shared between accordion and flat layouts).
  const renderRunSection = () => (
    <>
      <Button
        size="sm"
        className="w-full rounded-lg"
        onClick={handleRunTest}
        disabled={loading || !canRun() || planLapsed}
        title={planLapsed ? lapsedTitle : undefined}
      >
        {loading ? (
          <>
            <CircleNotch className="w-3.5 h-3.5 animate-spin mr-1.5" />
            Running... ({(elapsedMs / 1000).toFixed(1)}s)
          </>
        ) : (
          <>
            <Play className="w-3.5 h-3.5 mr-1.5" />
            Run Test
          </>
        )}
      </Button>

      {testResult && (() => {
        const details = testResult.result.details as Record<string, any> | undefined
        // Truthful outcome: the API `success` flag plus any error signal, so a
        // send that fails without raising still reads as a failure (red).
        const failed = !testResult.success || !!testResult.result.error || !!details?.error || !!testResult.result.requestFailed
        return (
        <div className="mt-3 rounded-lg border border-border/50 bg-card dark:bg-white/[0.02] dark:border-white/[0.08] p-4">
          {/* Status header — neutral surface, tone lives in the chip + title */}
          <div className="flex items-start gap-2.5">
            <div className={`h-7 w-7 rounded-md flex items-center justify-center flex-shrink-0 ${
              failed
                ? 'bg-red-500/10 text-red-600 dark:bg-red-500/[0.14] dark:text-red-400'
                : 'bg-emerald-500/10 text-emerald-600 dark:bg-emerald-500/[0.14] dark:text-emerald-400'
            }`}>
              {failed ? <XCircle className="w-4 h-4" weight="fill" /> : <CheckCircle className="w-4 h-4" weight="fill" />}
            </div>
            <div className="min-w-0 flex-1">
              <p className={`text-sm font-medium leading-tight ${
                failed ? 'text-red-700 dark:text-red-400' : 'text-emerald-700 dark:text-emerald-400'
              }`}>
                {testResult.result.message}
              </p>
              <div className="flex items-center gap-1.5 text-xs text-muted-foreground mt-1">
                <Clock className="w-3 h-3" />
                <span className="tabular-nums">{testResult.duration_ms}ms</span>
              </div>
            </div>
          </div>

          {failed && testResult.result.requestFailed ? null : failed ? (
            /* On failure show only the (humanized) error — no subject/recipient dump */
            (() => {
              const err = humanizeSendError(testResult.result.error || (details?.error as string | undefined))
              return (
                <div className="mt-2.5 space-y-1">
                  <p className="text-[13px] leading-relaxed text-foreground/90 dark:text-[#d0d6e0]">
                    {err.message}
                  </p>
                  {err.technical && (
                    <p className="text-[11px] font-mono text-muted-foreground break-words">
                      {err.technical}
                    </p>
                  )}
                </div>
              )
            })()
          ) : (
            <>
              {nodeType === 'generateDeck' && testResult.success && details?.material_id && (
                <div className="mt-3">
                  <AttachedMaterialPreview
                    materialId={details.material_id as number}
                    title={details.title as string | undefined}
                    filename={details.filename as string | undefined}
                    contentType={details.content_type as string | undefined}
                  />
                </div>
              )}

              {nodeType === 'enrichContacts' && details?.enrichment_data && (
                <div className="mt-3">
                  <EnrichmentResultView
                    data={details.enrichment_data}
                    provider={details.provider as string | undefined}
                    model={details.model as string | undefined}
                    status={details.enrichment_status as string | undefined}
                  />
                </div>
              )}

              {details && nodeType !== 'generateDeck' &&
                !(nodeType === 'enrichContacts' && details.enrichment_data) && (
                <ResultMeta details={details} />
              )}
            </>
          )}
        </div>
        )
      })()}
    </>
  )

  // Shared config + Run Test body, used by both the always-open (playground)
  // layout and the collapsible (canvas) accordion.
  const panelBody = (
    <div className="p-4 space-y-4">
      {/* Node-specific config */}
      {(nodeType === 'sendEmail' || (nodeType === 'internalAlert' && nodeData.internal_alert_config?.channel === 'email')) && (
        <SendEmailTestConfig config={emailConfig} onChange={setEmailConfig} hideRealContact={alwaysOpen} />
      )}
      {nodeType === 'smsMessage' && (
        <SmsTestConfig config={smsConfig} onChange={setSmsConfig} />
      )}
      {nodeType === 'linkedInAction' && <LinkedInTestConfig />}

      {/* Contact picker — required for some node types, optional for
          sendEmail/smsMessage where it's only used to render
          {{contact.*}} variables (not the recipient). */}
      {!preselectedContactId && (needsContact || showOptionalContactPicker) && (
        <div>
          <label className="block text-xs font-medium text-muted-foreground mb-1.5">
            {needsContact
              ? 'Contact'
              : 'Render variables using (optional)'}
          </label>
          <Select
            value={selectedContactId ? String(selectedContactId) : ''}
            onChange={v => setSelectedContactId(v ? Number(v) : undefined)}
            options={contacts.map(c => ({
              value: String(c.id),
              label: c.full_name ? `${c.full_name} (${c.email})` : c.email,
            }))}
            placeholder={needsContact ? 'Select contact...' : 'None — variables stay empty'}
            ariaLabel={needsContact ? 'Contact' : 'Render variables using'}
            className="w-full"
          />
          {!needsContact && (
            <p className="mt-1 text-xs text-muted-foreground">
              Pick a contact to substitute <code>{'{{contact.*}}'}</code> variables. The email still goes to your selected recipient.
            </p>
          )}
        </div>
      )}

      {renderRunSection()}
    </div>
  )

  // generateDeck (Collateral): flat layout — no "Test This Node" accordion shell,
  // just the Run Test button + result inline. No test-config / contact-picker
  // UI applies to this node type, so the accordion adds nothing but a click.
  if (nodeType === 'generateDeck') {
    return (
      <div className="mt-4">
        {renderRunSection()}
      </div>
    )
  }

  // Playground layout: always open, config + a single "Run Test" button (no
  // separate "Test This Node" trigger). The header is a plain label, so the
  // only actionable button is Run Test.
  if (alwaysOpen) {
    return (
      <div className="mt-4 border border-border/60 rounded-xl overflow-hidden shadow-card">
        <div className="w-full px-4 py-2.5 bg-muted/30 flex items-center gap-2 text-xs font-medium text-primary">
          <Play className="w-3.5 h-3.5" />
          Test this node
        </div>
        {panelBody}
      </div>
    )
  }

  if (!isOpen) {
    return (
      <button
        onClick={handleOpen}
        className="w-full mt-4 py-2.5 px-3.5 text-xs font-medium text-primary bg-primary/10 border border-border/50 rounded-xl hover:bg-primary/15 hover:border-border/80 transition-all duration-150 flex items-center justify-center gap-2"
      >
        <Play className="w-3.5 h-3.5" />
        Test This Node
      </button>
    )
  }

  return (
    <div className="mt-4 border border-border/60 rounded-xl overflow-hidden shadow-card">
      {/* Header */}
      <button
        onClick={() => setIsOpen(false)}
        className="w-full px-4 py-2.5 bg-muted/30 flex items-center justify-between text-xs font-medium text-primary"
      >
        <span className="flex items-center gap-2">
          <Play className="w-3.5 h-3.5" />
          Test This Node
        </span>
        <CaretUp className="w-3.5 h-3.5" />
      </button>

      {panelBody}
    </div>
  )
}

interface MaterialSlide { image: string; notes?: string }

const TEXT_PREVIEW_TYPES = new Set(['text', 'markdown', 'csv', 'docx', 'excel'])

const AttachedMaterialPreview: React.FC<{
  materialId: number
  title?: string
  filename?: string
  contentType?: string
}> = ({ materialId, title, filename, contentType }) => {
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  // PDF
  const [previewUrl, setPreviewUrl] = useState<string | null>(null)
  // PPTX
  const [slides, setSlides] = useState<MaterialSlide[] | null>(null)
  const [currentSlide, setCurrentSlide] = useState(0)
  // Text-like (docx, csv, text, markdown, excel)
  const [extractedText, setExtractedText] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    let createdUrl: string | null = null

    setLoading(true)
    setError(null)
    setPreviewUrl(null)
    setSlides(null)
    setExtractedText(null)
    setCurrentSlide(0)

    const ct = contentType || ''

    const load = async () => {
      try {
        if (ct === 'pdf') {
          const url = await materialsApi.getPreviewBlob(materialId)
          if (cancelled) {
            URL.revokeObjectURL(url)
            return
          }
          createdUrl = url
          setPreviewUrl(url)
        } else if (ct === 'pptx') {
          const res = await materialsApi.getSlides(materialId)
          if (cancelled) return
          setSlides((res.data?.slides as MaterialSlide[]) || [])
        } else if (TEXT_PREVIEW_TYPES.has(ct)) {
          const res = await materialsApi.getDetail(materialId)
          if (cancelled) return
          setExtractedText(res.data?.extracted_text || '')
        } else {
          // Unknown type — try the raw blob and hope the browser can render it.
          const url = await materialsApi.getPreviewBlob(materialId)
          if (cancelled) {
            URL.revokeObjectURL(url)
            return
          }
          createdUrl = url
          setPreviewUrl(url)
        }
      } catch (err: any) {
        if (!cancelled) {
          setError(err?.response?.data?.detail || 'Could not load preview for this file.')
        }
      } finally {
        if (!cancelled) setLoading(false)
      }
    }

    load()

    return () => {
      cancelled = true
      if (createdUrl) URL.revokeObjectURL(createdUrl)
    }
  }, [materialId, contentType])

  const [isExpanded, setIsExpanded] = useState(false)

  // Close modal on Escape; use window keydown listener (native DOM API).
  useEffect(() => {
    if (!isExpanded) return
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setIsExpanded(false) }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [isExpanded])

  const renderBody = (expanded: boolean) => {
    const frameClass = expanded ? 'w-full h-[80vh]' : 'w-full h-[400px]'
    const imgMaxH = expanded ? 'max-h-[70vh]' : 'max-h-[340px]'
    const textMaxH = expanded ? 'max-h-[75vh]' : 'max-h-[400px]'
    const textBytes = expanded ? 50000 : 8000

    if (loading) {
      return (
        <div className="h-40 flex items-center justify-center gap-2 text-xs text-muted-foreground">
          <CircleNotch className="w-4 h-4 animate-spin" /> Loading preview...
        </div>
      )
    }
    if (error) {
      return (
        <div className="p-4 flex flex-col items-center gap-2 text-center">
          <FileText className="w-8 h-8 text-muted-foreground/60" />
          <p className="text-xs text-muted-foreground">{error}</p>
        </div>
      )
    }
    if (previewUrl && contentType === 'pdf') {
      return <iframe src={previewUrl} className={frameClass} title="Material preview" />
    }
    if (slides && slides.length > 0) {
      return (
        <div>
          <div className="flex items-center justify-center bg-gray-900 p-3">
            <img
              src={slides[currentSlide]?.image}
              alt={`Slide ${currentSlide + 1}`}
              className={`w-full rounded-md shadow-lg ${imgMaxH} object-contain`}
            />
          </div>
          <div className="flex items-center justify-between px-3 py-2 bg-gray-800/80">
            <button
              type="button"
              disabled={currentSlide === 0}
              onClick={() => setCurrentSlide(s => s - 1)}
              className="px-2.5 py-1 text-[11px] rounded-md text-gray-400 hover:text-white hover:bg-gray-700 disabled:opacity-30 disabled:cursor-not-allowed transition-all duration-150"
            >
              Previous
            </button>
            <span className="text-[11px] text-gray-400">
              <span className="font-medium text-gray-200">{currentSlide + 1}</span> / {slides.length}
            </span>
            <button
              type="button"
              disabled={currentSlide >= slides.length - 1}
              onClick={() => setCurrentSlide(s => s + 1)}
              className="px-2.5 py-1 text-[11px] rounded-md text-gray-400 hover:text-white hover:bg-gray-700 disabled:opacity-30 disabled:cursor-not-allowed transition-all duration-150"
            >
              Next
            </button>
          </div>
        </div>
      )
    }
    if (extractedText !== null) {
      return extractedText ? (
        <pre className={`text-[11px] text-muted-foreground whitespace-pre-wrap p-3 ${textMaxH} overflow-y-auto font-mono leading-relaxed`}>
          {extractedText.length > textBytes ? extractedText.slice(0, textBytes) + '\n\n... (truncated)' : extractedText}
        </pre>
      ) : (
        <div className="p-4 flex flex-col items-center gap-2 text-center">
          <FileText className="w-8 h-8 text-muted-foreground/60" />
          <p className="text-xs text-muted-foreground">No extracted text available for this file.</p>
        </div>
      )
    }
    if (previewUrl) {
      return <iframe src={previewUrl} className={frameClass} title="Material preview" />
    }
    return null
  }

  const headerLabel = title || filename || 'Attachment'

  return (
    <>
      <div className="mt-3 rounded-lg border border-border/50 bg-card overflow-hidden">
        <div className="px-3 py-2 bg-muted/30 flex items-center gap-2 text-xs">
          <Paperclip className="w-3.5 h-3.5 text-orange-500" />
          <span className="font-medium text-foreground truncate">{headerLabel}</span>
          {contentType && <span className="uppercase text-[10px] text-muted-foreground">{contentType}</span>}
          {/* The playground could preview a generated deck but never hand it
              over (AB#22384). Routed through the shared downloadMaterial
              helper rather than an <a href> to the API: the download endpoint
              authenticates on the JWT *header*, so a plain link or
              window.open arrives unauthenticated and 401s. */}
          <Tooltip label="Download">
            <button
              type="button"
              onClick={() => downloadMaterial(materialId, filename)}
              disabled={loading || !!error}
              aria-label="Download attachment"
              className="ml-auto p-1 rounded-md text-muted-foreground hover:text-foreground hover:bg-accent/40 disabled:opacity-40 disabled:cursor-not-allowed transition-all duration-150"
            >
              <DownloadSimple className="w-3.5 h-3.5" />
            </button>
          </Tooltip>
          <Tooltip label="Expand preview">
            <button
              type="button"
              onClick={() => setIsExpanded(true)}
              disabled={loading || !!error}
              aria-label="Expand preview"
              className="p-1 rounded-md text-muted-foreground hover:text-foreground hover:bg-accent/40 disabled:opacity-40 disabled:cursor-not-allowed transition-all duration-150"
            >
              <ArrowsOut className="w-3.5 h-3.5" />
            </button>
          </Tooltip>
        </div>
        {renderBody(false)}
      </div>

      {isExpanded && typeof document !== 'undefined' && createPortal(
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center p-4"
          role="dialog"
          aria-modal="true"
          aria-label={`Preview of ${headerLabel}`}
        >
          <div
            className="absolute inset-0 bg-black/60 backdrop-blur-[3px] dark:bg-black/75"
            onClick={() => setIsExpanded(false)}
            aria-hidden="true"
          />
          <div className="relative w-full max-w-5xl max-h-[90vh] rounded-2xl border border-border/60 bg-card shadow-2xl overflow-hidden flex flex-col">
            <div className="px-4 py-2.5 bg-muted/30 border-b border-border/50 flex items-center gap-2 text-sm">
              <Paperclip className="w-4 h-4 text-orange-500" />
              <span className="font-medium text-foreground truncate flex-1">{headerLabel}</span>
              {contentType && <span className="uppercase text-[10px] text-muted-foreground">{contentType}</span>}
              <Tooltip label="Download">
                <button
                  type="button"
                  onClick={() => downloadMaterial(materialId, filename)}
                  aria-label="Download attachment"
                  className="p-1 rounded-md text-muted-foreground hover:text-foreground hover:bg-accent/40 transition-all duration-150"
                >
                  <DownloadSimple className="w-4 h-4" />
                </button>
              </Tooltip>
              <Tooltip label="Close preview">
                <button
                  type="button"
                  onClick={() => setIsExpanded(false)}
                  aria-label="Close preview"
                  className="p-1 rounded-md text-muted-foreground hover:text-foreground hover:bg-accent/40 transition-all duration-150"
                >
                  <X className="w-4 h-4" />
                </button>
              </Tooltip>
            </div>
            <div className="flex-1 overflow-auto">
              {renderBody(true)}
            </div>
          </div>
        </div>,
        document.body,
      )}
    </>
  )
}
